import { useEffect, useState, type ReactElement } from 'react';
import { api, type SchluesselView, type User } from '../api';
import { useEditor } from '../store';
import { Sheet } from './Sheet';
import { FARBEN, KEY_SCHEMES, keyHints, farbeVon, type EditorAction } from '@schnitt/shared';
import { bildUrl, zuschneiden } from '../profilbild';
import { setKeyScheme, useKeyScheme } from '../keyScheme';
import { THEMEN, setThema, useThema } from '../thema';
import { LEINWAENDE, setLeinwand, useLeinwand } from '../leinwandwahl';
import { TRANSPORT_LAGEN, setTransportLage, useTransportLage } from '../transportlage';
import { ausschalten, einschalten, stand, type PushStand } from '../push';

/** Die Handlungen in Saschas Worten, nicht in denen des Programms. */
const BEFEHL: Record<EditorAction, string> = {
  play: 'Abspielen / Anhalten',
  undo: 'Rückgängig',
  redo: 'Wiederherstellen',
  copy: 'Kopieren',
  paste: 'Einfügen',
  duplicate: 'Duplizieren',
  delete: 'Löschen',
  split: 'Trennen',
  frameBack: 'Ein Bild zurück',
  frameForward: 'Ein Bild vor',
  jumpBack: 'Sprung zurück',
  jumpForward: 'Sprung vor',
  prevCut: 'Zum vorigen Schnitt',
  nextCut: 'Zum nächsten Schnitt',
  keyframe: 'Keyframe setzen oder wegnehmen',
  prevKeyframe: 'Zum vorigen Keyframe',
  nextKeyframe: 'Zum nächsten Keyframe',
  bereichVon: 'Bereich beginnt hier',
  bereichBis: 'Bereich endet hier',
};

/** One's own account: the name colleagues see, and a new password. */
export function AccountSheet({ user, onClose, onChanged }: { user: User; onClose: () => void; onChanged: (user: User) => void }): ReactElement {
  const scheme = useKeyScheme();
  const thema = useThema();
  const leinwand = useLeinwand();
  const transportLage = useTransportLage();
  const toast = useEditor((s) => s.toast);
  // 7.67: Schlüssel für Werkzeuge. Die Liste ohne Schlüssel, und der eine,
  // der gerade angelegt wurde -- er steht nur jetzt hier.
  const [schluessel, setSchluessel] = useState<SchluesselView[] | null>(null);
  const [schluesselName, setSchluesselName] = useState('');
  const [neuerSchluessel, setNeuerSchluessel] = useState<{ name: string; token: string } | null>(null);
  useEffect(() => {
    void api
      .schluessel()
      .then((r) => setSchluessel(r.schluessel))
      .catch(() => setSchluessel([]));
  }, []);
  const schluesselAnlegen = async () => {
    const name = schluesselName.trim();
    if (!name) return;
    try {
      const { schluessel: neu } = await api.schluesselAnlegen(name);
      setNeuerSchluessel({ name: neu.name, token: neu.token });
      setSchluesselName('');
      setSchluessel((alt) => [{ id: neu.id, name: neu.name, created_at: new Date().toISOString(), last_used_at: null }, ...(alt ?? [])]);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Ging nicht.');
    }
  };
  const schluesselWiderrufen = async (id: string) => {
    try {
      await api.schluesselLoeschen(id);
      setSchluessel((alt) => (alt ?? []).filter((k) => k.id !== id));
      setNeuerSchluessel(null);
      toast('success', 'Schlüssel widerrufen.');
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Ging nicht.');
    }
  };
  const [push, setPush] = useState<PushStand | null>(null);
  const [pushLaeuft, setPushLaeuft] = useState(false);
  useEffect(() => {
    void stand().then(setPush);
  }, []);
  const [name, setName] = useState(user.name);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const rename = async () => {
    if (name.trim() === user.name || !name.trim()) return;
    setBusy(true);
    try {
      const res = await api.updateMe({ name: name.trim() });
      onChanged(res.user);
      setNote({ ok: true, text: 'Name geändert.' });
    } catch (err) {
      setNote({ ok: false, text: err instanceof Error ? err.message : 'Umbenennen fehlgeschlagen.' });
    } finally {
      setBusy(false);
    }
  };

  const bildWaehlen = async (datei: File | undefined) => {
    if (!datei) return;
    setBusy(true);
    setNote(null);
    try {
      // Zuschneiden hier, nicht dort: der Browser dekodiert, was er auch
      // anzeigen kann -- ein HEIC vom iPhone zum Beispiel.
      const res = await api.setzeProfilbild(await zuschneiden(datei));
      onChanged(res.user);
      setNote({ ok: true, text: 'Bild gesetzt.' });
    } catch (err) {
      setNote({ ok: false, text: err instanceof Error ? err.message : 'Das Bild ging nicht durch.' });
    } finally {
      setBusy(false);
    }
  };

  const bildLoeschen = async () => {
    setBusy(true);
    try {
      const res = await api.loescheProfilbild();
      onChanged(res.user);
      setNote({ ok: true, text: 'Bild entfernt — es bleibt der Buchstabe.' });
    } catch (err) {
      setNote({ ok: false, text: err instanceof Error ? err.message : 'Das hat nicht geklappt.' });
    } finally {
      setBusy(false);
    }
  };

  const waehleFarbe = async (farbe: string) => {
    if ((user.color ?? '') === farbe) return;
    setBusy(true);
    try {
      const res = await api.updateMe({ color: farbe });
      onChanged(res.user);
      setNote({ ok: true, text: farbe ? 'Farbe geändert.' : 'Farbe wird wieder ausgerechnet.' });
    } catch (err) {
      setNote({ ok: false, text: err instanceof Error ? err.message : 'Das hat nicht geklappt.' });
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== again) {
      setNote({ ok: false, text: 'Die beiden neuen Passwörter sind nicht gleich.' });
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(current, next);
      setCurrent('');
      setNext('');
      setAgain('');
      setNote({ ok: true, text: 'Passwort geändert. Andere Geräte sind abgemeldet.' });
    } catch (err) {
      setNote({ ok: false, text: err instanceof Error ? err.message : 'Passwort konnte nicht geändert werden.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title="Mein Konto" onClose={onClose}>
      <div className="card" style={{ margin: '0 0 14px' }}>
        <div className="spread">
          <div>
            <div className="title">{user.email}</div>
            <div className="sub">{user.role === 'admin' ? 'Admin — darf Konten und Einstellungen verwalten' : 'Mitglied — darf Projekte anlegen und schneiden'}</div>
          </div>
        </div>
      </div>
      <label className="field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => void rename()} maxLength={120} />
      </label>

      {/* Das Profilbild (7.45b). Der Buchstabe bleibt der Rückfall -- für alle
          ohne Bild und für den Augenblick, in dem eines noch lädt. */}
      <div className="field">
        <span>Dein Bild</span>
        <div className="spread" style={{ alignItems: 'center', gap: 12 }}>
          {bildUrl(user.userId, user.avatarAt) ? (
            <img
              src={bildUrl(user.userId, user.avatarAt) ?? ''}
              alt=""
              width={56}
              height={56}
              style={{ borderRadius: '50%', objectFit: 'cover' }}
              data-profilbild
            />
          ) : (
            <span
              className="avatar"
              style={{ width: 56, height: 56, fontSize: 22, background: farbeVon(user.userId, user.color) }}
              aria-hidden="true"
              data-profilbild-leer
            >
              {(user.name || user.email).trim().charAt(0).toUpperCase() || '?'}
            </span>
          )}
          <div className="stack" style={{ flex: 1 }}>
            <label className="chip" style={{ textAlign: 'center', cursor: 'pointer' }}>
              {user.avatarAt ? 'Anderes Bild wählen' : 'Bild wählen'}
              <input
                type="file"
                accept="image/*"
                hidden
                disabled={busy}
                data-bildwahl
                onChange={(e) => {
                  void bildWaehlen(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            {user.avatarAt ? (
              <button className="ghost small" disabled={busy} onClick={() => void bildLoeschen()} data-bild-weg>
                Bild entfernen
              </button>
            ) : null}
          </div>
        </div>
        <p className="hint">
          Quadratisch aus der Mitte, {128} px. Zugeschnitten wird schon hier im Browser — was hochgeht, ist das kleine
          Bild und nicht das ganze Foto. Ohne Bild steht dein Anfangsbuchstabe da.
        </p>
      </div>

      {/* Die eigene Farbe (7.45). „Wo kann ich eine Userfarbe festlegen?“
          (Sascha, 7. September) -- bisher nirgends: sie wurde aus der
          Nutzer-Kennung gerechnet, sieben feste Werte, und wer welchen bekam,
          war Zufall. Sie steht am Kollegen-Kürzel und am fremden Abspielkopf,
          also genau dort, wo man einander auseinanderhalten will. */}
      <div className="field">
        <span>Deine Farbe</span>
        <div className="chips" data-farbwahl>
          {FARBEN.map((farbe) => (
            <button
              key={farbe}
              className={`farbtupfer ${user.color === farbe ? 'active' : ''}`}
              style={{ background: farbe }}
              aria-label={`Farbe ${farbe}`}
              aria-pressed={user.color === farbe}
              data-farbe={farbe}
              onClick={() => void waehleFarbe(farbe)}
            />
          ))}
          <button
            className={`chip ${user.color ? '' : 'active'}`}
            data-farbe=""
            onClick={() => void waehleFarbe('')}
            title="Dann rechnet der Schnittplatz sie wieder aus deiner Kennung aus."
          >
            Von selbst
          </button>
        </div>
        <p className="hint">
          Daran erkennen dich die anderen: am Kürzel oben und am Abspielkopf im Schnitt. Ohne eigene Wahl rechnet der
          Schnittplatz eine aus — dann ist sie fest, aber zufällig.
        </p>
      </div>
      <form onSubmit={changePassword}>
        <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Passwort ändern</h3>
        <label className="field">
          <span>Bisheriges Passwort</span>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
        </label>
        <label className="field">
          <span>Neues Passwort</span>
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" minLength={12} required />
        </label>
        <label className="field">
          <span>Noch einmal</span>
          <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" required />
        </label>
        <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          Passwort ändern
        </button>
      </form>
      {note ? <p className={note.ok ? 'hint' : 'error-text'} data-account-note>{note.text}</p> : null}
      <p className="hint">E-Mail und Rolle ändert ein Admin unter „Verwaltung → Konten“.</p>
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '22px 0 8px' }}>Aussehen</h3>
      <div className="chips" data-thema-wahl>
        {THEMEN.map((t) => (
          <button
            key={t.id}
            className={`chip ${thema === t.id ? 'active' : ''}`}
            data-thema={t.id}
            onClick={() => setThema(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {/* Die Leinwand bleibt in beiden dunkel -- das ist Absicht und keine
          vergessene Farbe, also steht es da. */}
      <p className="hint">Gilt für diesen Browser. Die Leinwand bleibt in beiden dunkel.</p>

      {/* 7.69e: der Schalter für die Leinwand, die selbst malt. Voreinstellung
          ist der alte Weg; wer umschaltet, lädt neu -- die Vorschau baut sich
          beim Start auf den einen oder den anderen Weg. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '22px 0 8px' }}>Leinwand</h3>
      <div className="chips" data-leinwand-wahl>
        {LEINWAENDE.map((l) => (
          <button
            key={l.id}
            className={`chip ${leinwand === l.id ? 'active' : ''}`}
            data-leinwand={l.id}
            onClick={() => {
              if (l.id === leinwand) return;
              setLeinwand(l.id);
              window.location.reload();
            }}
          >
            {l.label}
          </button>
        ))}
      </div>
      <p className="hint">
        „Grafikkarte“ malt Bild, Ebenen, Looks und Text in eine Fläche statt in viele Elemente — auf dem Telefon
        flüssiger, im Export ändert sich nichts. Gilt für diesen Browser und lädt die Seite neu.
      </p>

      {/* 7.62: wo die Leiste mit dem Abspielknopf steht. Ziehen am Griff
          setzt sie um; hier ist der Weg zurück, ohne zu ziehen. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '22px 0 8px' }}>Transportleiste</h3>
      <div className="chips" data-transport-wahl>
        {TRANSPORT_LAGEN.map((l) => (
          <button
            key={l.id}
            className={`chip ${transportLage === l.id ? 'active' : ''}`}
            data-transport={l.id}
            onClick={() => setTransportLage(l.id)}
          >
            {l.label}
          </button>
        ))}
      </div>
      <p className="hint">Gilt für diesen Browser. Am Griff links in der Leiste lässt sie sich auch ziehen.</p>

      {/* 7.67: Schlüssel für Werkzeuge von aussen -- der Schnittplatz als
          MCP-Server. Ein Schlüssel ist die Person mit genau ihren Rechten,
          wird ein einziges Mal gezeigt und lässt sich einzeln widerrufen. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '22px 0 8px' }}>Schlüssel für Werkzeuge</h3>
      <p className="hint" style={{ marginTop: 0 }}>
        Damit ruft ein Werkzeug von aussen — etwa Claude über MCP — den Schnittplatz mit deinen Rechten. Wie das geht, steht in
        der Anleitung <code>docs/MCP.md</code>.
      </p>
      {neuerSchluessel ? (
        <div className="field" data-schluessel-neu>
          <span>„{neuerSchluessel.name}“ — nur jetzt zu sehen, danach nicht mehr</span>
          <input readOnly value={neuerSchluessel.token} data-schluessel-token onFocus={(e) => e.currentTarget.select()} />
        </div>
      ) : null}
      {schluessel && schluessel.length > 0 ? (
        <ul className="list" data-schluessel-liste>
          {schluessel.map((k) => (
            <li key={k.id} className="spread" data-schluessel={k.id}>
              <span>
                {k.name}
                <small style={{ color: 'var(--muted)', marginLeft: 8 }}>
                  {k.last_used_at ? `zuletzt ${new Date(k.last_used_at).toLocaleDateString('de-DE')}` : 'noch nie benutzt'}
                </small>
              </span>
              <button className="ghost small" data-schluessel-loeschen onClick={() => void schluesselWiderrufen(k.id)}>
                Widerrufen
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="spread" style={{ gap: 8 }}>
        <input
          placeholder="Wofür, etwa „Claude am Rechner“"
          value={schluesselName}
          data-schluessel-name
          onChange={(e) => setSchluesselName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void schluesselAnlegen();
          }}
        />
        <button className="ghost small" data-schluessel-anlegen disabled={!schluesselName.trim()} onClick={() => void schluesselAnlegen()}>
          Anlegen
        </button>
      </div>

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '22px 0 8px' }}>Benachrichtigungen</h3>
      {push === null ? (
        <p className="hint laedt" style={{ marginTop: 0 }}>Einen Moment…</p>
      ) : push === 'nicht-vom-home-bildschirm' ? (
        /* Auf dem iPhone gibt es Push nur vom Home-Bildschirm. Das ist keine
           kaputte App, sondern eine Bedingung -- also steht sie da, samt Weg
           dorthin. */
        <p className="hint" style={{ marginTop: 0 }}>
          Auf dem iPhone nur vom Home-Bildschirm. Über <strong>Teilen › Zum Home-Bildschirm</strong>, dann hier wieder
          nachsehen.
        </p>
      ) : push === 'geht-nicht' ? (
        <p className="hint" style={{ marginTop: 0 }}>Dieser Browser kann das nicht.</p>
      ) : push === 'noch-nicht' ? (
        /* Der Browser kann es, der Service Worker läuft nur noch nicht. Das
           passiert beim allerersten Start vom Home-Bildschirm. */
        <p className="hint" style={{ marginTop: 0 }}>Der Schnittplatz startet noch. Gleich nochmal.</p>
      ) : push === 'abgelehnt' ? (
        <p className="hint" style={{ marginTop: 0 }}>
          Abgelehnt. Wieder erlauben geht nur in den Einstellungen des Browsers für diese Seite.
        </p>
      ) : (
        <>
          <div className="stack">
            <button
              className={push === 'an' ? '' : 'primary'}
              data-push-schalter
              disabled={pushLaeuft}
              onClick={() => {
                setPushLaeuft(true);
                // Aus der Berührung heraus: Safari beantwortet ein
                // requestPermission ohne Geste mit einem Nein, das bleibt.
                void (push === 'an' ? ausschalten() : einschalten())
                  .then((neu) => {
                    setPush(neu);
                    if (neu === 'an') setNote({ ok: true, text: 'Benachrichtigungen an.' });
                    if (neu === 'abgelehnt') setNote({ ok: false, text: 'Der Browser hat abgelehnt.' });
                  })
                  .catch((err: unknown) =>
                    setNote({ ok: false, text: err instanceof Error ? err.message : 'Ging nicht.' }),
                  )
                  .finally(() => setPushLaeuft(false));
              }}
            >
              {push === 'an' ? 'Benachrichtigungen aus' : 'Benachrichtigungen an'}
            </button>
            {push === 'an' ? (
              /* Ohne Probe merkt man erst beim nächsten Hinweis -- vielleicht
                 morgen --, dass nichts ankommt. */
              <button
                className="ghost"
                data-push-probe
                disabled={pushLaeuft}
                onClick={() => {
                  setPushLaeuft(true);
                  void api
                    .pushProbe()
                    .then((r) => setNote({ ok: true, text: `Geschickt an ${r.geraete === 1 ? 'ein Gerät' : `${r.geraete} Geräte`}.` }))
                    .catch((err: unknown) =>
                      setNote({ ok: false, text: err instanceof Error ? err.message : 'Ging nicht.' }),
                    )
                    .finally(() => setPushLaeuft(false));
                }}
              >
                Probe schicken
              </button>
            ) : null}
          </div>
          <p className="hint">Neuer Hinweis, Freigabe, fertiger Export. Gilt für dieses Gerät.</p>
        </>
      )}

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '22px 0 8px' }}>Tastenbelegung</h3>
      <p className="hint" style={{ marginTop: 0 }}>
        Gilt für diesen Browser, nicht für das Konto. Nachgebildet sind die wichtigsten Befehle.
      </p>
      <div className="chips">
        {KEY_SCHEMES.map((s) => (
          <button
            key={s.id}
            className={`chip ${scheme === s.id ? 'active' : ''}`}
            data-scheme={s.id}
            onClick={() => setKeyScheme(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <p className="hint">{KEY_SCHEMES.find((s) => s.id === scheme)?.hint}</p>
      <div className="list" data-tastenliste>
        {keyHints(scheme).map((h) => (
          <div key={h.action} className="row" style={{ justifyContent: 'space-between' }}>
            <span>{BEFEHL[h.action]}</span>
            <kbd>{h.keys}</kbd>
          </div>
        ))}
      </div>
    </Sheet>
  );
}
